/**
 * Customer Feedback Admin Page
 * View and manage feedback submissions from center owners
 * Only accessible to the primary admin
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  MessageCircle, ThumbsUp, Lightbulb, AlertCircle, HelpCircle,
  Mail, Building2, User, Clock, CheckCircle, Eye, Trash2,
  ArrowLeft, RefreshCw, Filter, Search, ExternalLink, Copy
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_EMAIL = "todayskidz@comcast.net"; // Your admin email

const FEEDBACK_TYPES = {
  compliment: { label: "Compliment", icon: ThumbsUp, color: "text-green-600", bgColor: "bg-green-100" },
  suggestion: { label: "Suggestion", icon: Lightbulb, color: "text-amber-600", bgColor: "bg-amber-100" },
  issue: { label: "Issue", icon: AlertCircle, color: "text-red-600", bgColor: "bg-red-100" },
  question: { label: "Question", icon: HelpCircle, color: "text-blue-600", bgColor: "bg-blue-100" },
  other: { label: "Other", icon: MessageCircle, color: "text-purple-600", bgColor: "bg-purple-100" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "reviewed", label: "Reviewed", color: "bg-amber-100 text-amber-700" },
  { value: "responded", label: "Responded", color: "bg-green-100 text-green-700" },
  { value: "resolved", label: "Resolved", color: "bg-gray-100 text-gray-600" },
];

const PRIORITY_COLORS = {
  low: "bg-gray-100 text-gray-600",
  normal: "bg-blue-100 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-700 font-bold",
};

// Response templates for quick replies
const RESPONSE_TEMPLATES = {
  compliment: [
    {
      label: "Thank You",
      text: "Thank you so much for your kind words! It means a lot to us to hear positive feedback. We're committed to providing the best experience possible, and knowing we're on the right track motivates our entire team. We truly appreciate your support!"
    },
    {
      label: "Team Recognition",
      text: "Thank you for taking the time to share this wonderful feedback! We'll make sure to pass along your kind words to our team. It's feedback like yours that inspires us to continue delivering excellent service. Thank you for being part of our community!"
    }
  ],
  suggestion: [
    {
      label: "Great Idea",
      text: "Thank you for this excellent suggestion! We're always looking for ways to improve, and your idea is exactly the kind of feedback we value. We've added this to our development roadmap and will keep you updated on any progress. Your input helps make our service better for everyone!"
    },
    {
      label: "Under Review",
      text: "Thank you for sharing your suggestion with us! We've noted your idea and will be reviewing it with our team. While we can't guarantee immediate implementation, we truly value your input and will consider how it might fit into our future plans. We appreciate you helping us improve!"
    }
  ],
  issue: [
    {
      label: "Issue Resolved",
      text: "Thank you for bringing this to our attention. We've investigated the issue you reported and have implemented a fix. The problem should now be resolved. If you experience any further difficulties, please don't hesitate to reach out. We apologize for any inconvenience this may have caused."
    },
    {
      label: "Investigating",
      text: "Thank you for reporting this issue. We take all concerns seriously and our team is actively investigating the matter. We'll work to resolve this as quickly as possible and will follow up with you once we have more information. We appreciate your patience and understanding."
    },
    {
      label: "Need More Info",
      text: "Thank you for reaching out about this issue. To help us investigate further, could you please provide some additional details? Specifically, it would be helpful to know when this occurred and any specific steps that led to the problem. This information will help us resolve the issue more quickly."
    }
  ],
  question: [
    {
      label: "Question Answered",
      text: "Thank you for your question! I hope this response addresses your inquiry. If you need any further clarification or have additional questions, please don't hesitate to ask. We're here to help!"
    },
    {
      label: "Follow Up Needed",
      text: "Thank you for reaching out with your question. I'd like to provide you with the most accurate information possible. Could you please provide a bit more context about your specific situation? This will help me give you a more tailored response."
    }
  ],
  other: [
    {
      label: "General Thanks",
      text: "Thank you for reaching out to us! We appreciate you taking the time to contact us. If there's anything else we can help you with, please don't hesitate to let us know. We're always here to assist!"
    },
    {
      label: "Acknowledged",
      text: "Thank you for your message. We've received your communication and wanted to let you know that we're here if you need anything. Please feel free to reach out anytime with questions, feedback, or concerns."
    }
  ]
};

export default function CustomerFeedbackAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, reviewed: 0, responded: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Detail dialog
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check admin access
    if (user && user.email !== ADMIN_EMAIL) {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    
    fetchFeedback();
  }, [user, navigate]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterType !== "all") params.append("feedbackType", filterType);
      
      const res = await axios.get(`${API}/feedback/admin/all?${params.toString()}`);
      setFeedbackList(res.data.feedback || []);
      setStats(res.data.stats || { total: 0, new: 0, reviewed: 0, responded: 0, resolved: 0 });
    } catch (error) {
      console.error("Error fetching feedback:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (feedback) => {
    setSelectedFeedback(feedback);
    setAdminNotes(feedback.adminNotes || "");
    setResponseMessage(feedback.adminResponse || "");
    setSendEmailNotification(true); // Reset to default when opening new feedback
    setShowDetailDialog(true);
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API}/feedback/admin/${feedbackId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchFeedback();
      
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveNotes = async () => {
    if (!selectedFeedback) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/feedback/admin/${selectedFeedback.id}/notes?notes=${encodeURIComponent(adminNotes)}`);
      toast.success("Notes saved");
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const recordResponse = async () => {
    if (!selectedFeedback || !responseMessage.trim()) return;
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/feedback/admin/${selectedFeedback.id}/respond`, {
        responseMessage,
        sendEmail: sendEmailNotification
      });
      
      if (res.data.emailSent) {
        toast.success("Response saved and email sent to customer");
      } else {
        toast.success("Response recorded");
      }
      setShowDetailDialog(false);
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to record response");
    } finally {
      setSaving(false);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!confirm("Are you sure you want to delete this feedback?")) return;
    
    try {
      await axios.delete(`${API}/feedback/admin/${feedbackId}`);
      toast.success("Feedback deleted");
      setShowDetailDialog(false);
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to delete feedback");
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success("Email copied to clipboard");
  };

  const openEmailClient = (email, subject = "") => {
    window.location.href = `mailto:${email}?subject=Re: ${encodeURIComponent(subject)}`;
  };

  const filteredFeedback = feedbackList.filter(item => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        item.subject?.toLowerCase().includes(search) ||
        item.message?.toLowerCase().includes(search) ||
        item.userName?.toLowerCase().includes(search) ||
        item.centerName?.toLowerCase().includes(search) ||
        item.userEmail?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-800">Customer Feedback Admin</h1>
          </div>
          <Button onClick={fetchFeedback} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.new}</p>
              <p className="text-sm text-blue-600">New</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-700">{stats.reviewed}</p>
              <p className="text-sm text-amber-600">Reviewed</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{stats.responded}</p>
              <p className="text-sm text-green-600">Responded</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-700">{stats.resolved}</p>
              <p className="text-sm text-gray-600">Resolved</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(FEEDBACK_TYPES).map(([value, info]) => (
                    <SelectItem key={value} value={value}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button onClick={fetchFeedback} size="sm">Apply Filters</Button>
              
              <div className="flex-1"></div>
              
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        <Card>
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-lg">
              Feedback Submissions ({filteredFeedback.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading feedback...</p>
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No feedback found matching your filters.
              </div>
            ) : (
              <div className="divide-y">
                {filteredFeedback.map((item) => {
                  const typeInfo = FEEDBACK_TYPES[item.feedbackType] || FEEDBACK_TYPES.other;
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <div 
                      key={item.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetail(item)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${typeInfo.bgColor}`}>
                          <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-800">{item.subject}</span>
                            <Badge className={STATUS_OPTIONS.find(s => s.value === item.status)?.color || ""}>
                              {item.status}
                            </Badge>
                            {item.priority !== "normal" && (
                              <Badge className={PRIORITY_COLORS[item.priority]}>
                                {item.priority}
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 line-clamp-1 mb-2">{item.message}</p>
                          
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {item.centerName}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {item.userName}
                            </span>
                            <span className="flex items-center gap-1 text-indigo-600 font-medium">
                              <Mail className="w-3 h-3" />
                              {item.userEmail}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(item.createdAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(item); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedFeedback && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const typeInfo = FEEDBACK_TYPES[selectedFeedback.feedbackType] || FEEDBACK_TYPES.other;
                    const TypeIcon = typeInfo.icon;
                    return <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />;
                  })()}
                  {selectedFeedback.subject}
                </DialogTitle>
                <DialogDescription>
                  Submitted on {new Date(selectedFeedback.createdAt).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Customer Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span>{selectedFeedback.centerName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{selectedFeedback.userName}</span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-indigo-600 font-medium">{selectedFeedback.userEmail}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2"
                        onClick={() => copyEmail(selectedFeedback.userEmail)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-indigo-600"
                        onClick={() => openEmailClient(selectedFeedback.userEmail, selectedFeedback.subject)}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Reply
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label className="text-sm font-semibold">Message</Label>
                  <div className="mt-1 p-3 bg-white border rounded-lg text-sm whitespace-pre-wrap">
                    {selectedFeedback.message}
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-semibold">Status:</Label>
                  <Select 
                    value={selectedFeedback.status} 
                    onValueChange={(v) => updateStatus(selectedFeedback.id, v)}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <Badge className={opt.color}>{opt.label}</Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Admin Notes */}
                <div>
                  <Label className="text-sm font-semibold">Internal Notes (not visible to customer)</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this feedback..."
                    className="mt-1"
                  />
                  <Button 
                    onClick={saveNotes} 
                    disabled={saving}
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                  >
                    Save Notes
                  </Button>
                </div>

                {/* Response (visible to customer) */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold">Response to Customer (they can see this)</Label>
                  
                  {/* Template Quick Select */}
                  {selectedFeedback?.feedbackType && RESPONSE_TEMPLATES[selectedFeedback.feedbackType] && (
                    <div className="mt-2 mb-3">
                      <p className="text-xs text-gray-500 mb-2">Quick templates:</p>
                      <div className="flex flex-wrap gap-2">
                        {RESPONSE_TEMPLATES[selectedFeedback.feedbackType].map((template, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 bg-indigo-50 hover:bg-indigo-100 border-indigo-200"
                            onClick={() => setResponseMessage(template.text)}
                          >
                            {template.label}
                          </Button>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-7 text-gray-500"
                          onClick={() => setResponseMessage("")}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <Textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Enter your response to the customer..."
                    className="mt-1"
                    rows={5}
                  />
                  <div className="flex items-center gap-2 mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <input
                      type="checkbox"
                      id="sendEmailNotification"
                      checked={sendEmailNotification}
                      onChange={(e) => setSendEmailNotification(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="sendEmailNotification" className="flex items-center gap-2 text-sm cursor-pointer">
                      <Mail className="w-4 h-4 text-indigo-600" />
                      <span>Send email notification to <strong>{selectedFeedback?.userEmail}</strong></span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    When enabled, an email will be sent to the customer with your response.
                  </p>
                </div>
              </div>

              <DialogFooter className="flex justify-between mt-4">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => deleteFeedback(selectedFeedback.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={recordResponse}
                    disabled={saving || !responseMessage.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {saving ? "Saving..." : "Save Response"}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
